import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from './api';
import type { BookResult, Load, Quote, RateShopResult, RelayConfig } from './types';
import { LoadRail } from './components/LoadRail';
import { LoadDetail } from './components/LoadDetail';
import { RateTable } from './components/RateTable';
import { BookingPanel } from './components/BookingPanel';
import { money, plural, shortDate } from './lib/format';
import { EmbedView } from './components/EmbedView';
import { isEmbedMode, readEmbedContext } from './lib/embedParams';
import { BluestemLockup, PoweredByRsm, UserAvatar } from './components/Brand';

const SOURCE_LABEL: Record<string, { text: string; tone: string; title: string }> = {
  live: {
    text: 'Live carrier rates',
    tone: 'good',
    title: 'Returned by RateLinx from the carrier just now',
  },
  recorded: {
    text: 'Recorded response',
    tone: 'accent',
    title: 'A real carrier response captured earlier and replayed, rebased onto this ship date',
  },
  synthetic: {
    text: 'Modelled rates',
    tone: 'warning',
    title: 'Generated locally by the fallback rating model — not carrier data',
  },
};

/**
 * Two entry points share one bundle: the full site, and the carrier-options-only
 * view D365 embeds. Embed mode is decided once from the URL - see embedParams.
 */
export default function App() {
  const embed = useMemo(() => (isEmbedMode() ? readEmbedContext() : null), []);

  useEffect(() => {
    if (embed?.theme) document.documentElement.setAttribute('data-theme', embed.theme);
  }, [embed]);

  if (embed) return <EmbedView context={embed} />;
  return <FullApp />;
}

function FullApp() {
  const [config, setConfig] = useState<RelayConfig | null>(null);
  const [loads, setLoads] = useState<Load[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [result, setResult] = useState<RateShopResult | null>(null);
  const [booking, setBooking] = useState<BookResult | null>(null);

  const [rating, setRating] = useState(false);
  const [busyShipVia, setBusyShipVia] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => loads.find((l) => l.loadId === selectedId) ?? null,
    [loads, selectedId],
  );

  // --- Boot -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cfg, list] = await Promise.all([api.config(), api.loads()]);
        if (cancelled) return;
        setConfig(cfg);
        setLoads(list.loads);
        setSelectedId((current) => current ?? list.loads[0]?.loadId ?? null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Clear rating state whenever the chosen load changes.
  useEffect(() => {
    setResult(null);
    setBooking(null);
    setError(null);
  }, [selectedId]);

  const refreshLoad = useCallback((updated: Load) => {
    setLoads((current) => current.map((l) => (l.loadId === updated.loadId ? updated : l)));
  }, []);

  const runRateShop = useCallback(async () => {
    if (!selected) return;
    setRating(true);
    setError(null);
    setBooking(null);
    try {
      setResult(await api.rateShop(selected.loadId));
    } catch (err) {
      setError((err as Error).message);
      setResult(null);
    } finally {
      setRating(false);
    }
  }, [selected]);

  const quotePayload = (q: Quote) => ({
    shipVia: q.shipVia,
    serviceName: q.serviceName,
    rate: q.rate,
    deliveryDate: q.deliveryDate,
    transitDays: q.transitDays,
  });

  const handleSelect = useCallback(
    async (q: Quote) => {
      if (!selected) return;
      setBusyShipVia(q.shipVia);
      setError(null);
      try {
        refreshLoad(await api.select(selected.loadId, quotePayload(q)));
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusyShipVia(null);
      }
    },
    [selected, refreshLoad],
  );

  const handleBook = useCallback(
    async (q: Quote) => {
      if (!selected) return;
      setBusyShipVia(q.shipVia);
      setError(null);
      try {
        const booked = await api.book(selected.loadId, quotePayload(q));
        setBooking(booked);
        refreshLoad(booked.load);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusyShipVia(null);
      }
    },
    [selected, refreshLoad],
  );

  /** Clears every carrier choice - the "start the demo over" button. */
  const handleResetDemo = useCallback(async () => {
    setError(null);
    try {
      await api.reset();
      const list = await api.loads();
      setLoads(list.loads);
      setResult(null);
      setBooking(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const handleClearSelection = useCallback(async () => {
    if (!selected) return;
    try {
      refreshLoad(await api.clearSelection(selected.loadId));
      setBooking(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, [selected, refreshLoad]);

  // --- The headline the table is trying to make -----------------------------
  const insight = useMemo(() => {
    if (!result || result.quotes.length < 2) return null;
    const { analysis } = result;
    const cheapest = result.quotes.find((q) => q.shipVia === analysis.cheapestShipVia);
    const fastest = result.quotes.find((q) => q.shipVia === analysis.fastestShipVia);
    if (!cheapest || !fastest) return null;

    // When the required-by date rules out the cheapest service outright, that is
    // the story - what the promised date costs. It beats any commit-time nuance.
    const cheapestOverall = result.quotes.find((q) => q.shipVia === analysis.cheapestOverallShipVia);
    if (analysis.deadlinePremium && cheapestOverall && cheapestOverall !== cheapest) {
      return (
        <>
          <strong>{cheapestOverall.serviceName}</strong> at {money(cheapestOverall.rate)} is cheapest
          but misses the {shortDate(result.requiredByDate)} deadline. The cheapest service that
          actually makes it is <strong>{cheapest.serviceName}</strong> at {money(cheapest.rate)} — so
          this delivery date costs {money(analysis.deadlinePremium)}.
        </>
      );
    }

    // Ignore trivial premiums; a 33-cent gap is not an insight.
    if (
      analysis.cheapestTiesFastestOnDate &&
      cheapest !== fastest &&
      analysis.premiumOverCheapest !== null &&
      analysis.premiumOverCheapest >= 1
    ) {
      return (
        <>
          <strong>{cheapest.serviceName}</strong> arrives the same day as{' '}
          <strong>{fastest.serviceName}</strong> for {money(analysis.premiumOverCheapest)} less. The
          premium buys a {fastest.commitTime} commitment, not a faster day.
        </>
      );
    }

    if (analysis.dominatedCount > 0) {
      return (
        <>
          <strong>{plural(analysis.dominatedCount, 'service')}</strong> on this lane cost more{' '}
          <em>and</em> arrive later than {cheapest.serviceName}. Rate shopping removes them from the
          conversation.
        </>
      );
    }

    return (
      <>
        {money(analysis.spread)} separates the cheapest and dearest option on this lane, and{' '}
        <strong>{cheapest.serviceName}</strong> at {money(cheapest.rate)} still lands{' '}
        {shortDate(cheapest.deliveryDate)}.
      </>
    );
  }, [result]);

  const sourceMeta = result ? SOURCE_LABEL[result.rateSource] : null;
  const canBook = Boolean(config && (config.ratelinxMode !== 'live' || config.allowLiveBooking));

  return (
    <div className="app">
      <header className="masthead">
        <BluestemLockup />
        <span className="divider" aria-hidden="true" />
        <div>
          <h1>Load Rate Shop</h1>
          <div className="sub">Dynamics 365 F&amp;SC loads rated through RateLinx</div>
        </div>

        <div className="posture">
          {config && (
            <>
              <span className="chip" title="Where the load list comes from">
                Loads: {config.d365Source === 'seed' ? 'seeded' : 'D365 OData'}
              </span>
              <span
                className={`chip ${config.ratelinxMode === 'live' ? 'good' : ''}`}
                title={`RateLinx ${config.ratelinxBaseUrl}`}
              >
                <span className="dot" aria-hidden="true" />
                {config.ratelinxMode === 'live' ? 'Live RateLinx' : 'Fixture mode'}
              </span>
              <span
                className={`chip${config.ratelinxEnvironment === 'production' ? ' critical' : ''}`}
              >
                {config.ratelinxEnvironment}
              </span>
              {config.ratelinxMode === 'live' && (
                <span className={`chip ${config.allowLiveBooking ? 'critical' : ''}`}>
                  {config.allowLiveBooking ? 'Booking armed' : 'Booking locked'}
                </span>
              )}
              {config.user && (
                <span className="chip" title="Signed in via Cloudflare Access">
                  {config.user}
                </span>
              )}
              {!config.selectionsPersist && (
                <span className="chip warning" title="No KV namespace is bound, so carrier choices are lost between requests">
                  <span className="dot" aria-hidden="true" />
                  Selections not persisting
                </span>
              )}
              {loads.some((l) => l.selection) && (
                <button type="button" className="btn small" onClick={handleResetDemo}>
                  Reset demo
                </button>
              )}
            </>
          )}
          {/* RSM sponsor mark: right end of the ribbon, beside the avatar, behind a divider */}
          <span className="divider" aria-hidden="true" />
          <PoweredByRsm />
          <UserAvatar user={config?.user} />
        </div>
      </header>

      <div className="layout">
        <LoadRail loads={loads} selectedId={selectedId} onSelect={setSelectedId} />

        <main className="main">
          {error && (
            <div className="callout danger">
              <span className="icon" aria-hidden="true">
                !
              </span>
              <span>{error}</span>
            </div>
          )}

          {!selected && !error && <div className="empty">Loading loads…</div>}

          {selected && (
            <>
              <LoadDetail load={selected} />

              <div className="panel">
                <div className="panel-head">
                  <div>
                    <h2>Carrier options</h2>
                    <div className="hint">
                      {result
                        ? `${plural(result.quoteCount, 'service')} priced · required by ${shortDate(result.requiredByDate)}`
                        : 'Shop every eligible service for this load'}
                    </div>
                  </div>
                  <div className="posture">
                    {sourceMeta && (
                      <span className={`chip ${sourceMeta.tone}`} title={sourceMeta.title}>
                        <span className="dot" aria-hidden="true" />
                        {sourceMeta.text}
                      </span>
                    )}
                    {result && result.elapsedMs > 0 && (
                      <span className="chip">{result.elapsedMs} ms</span>
                    )}
                    {selected.selection && (
                      <button type="button" className="btn small" onClick={handleClearSelection}>
                        Clear choice
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn primary"
                      onClick={runRateShop}
                      disabled={rating}
                    >
                      {rating && <span className="spinner" />}
                      {result ? 'Re-shop' : 'Rate shop'}
                    </button>
                  </div>
                </div>

                {result && result.quotes.length > 0 && (
                  <div className="panel-body" style={{ paddingBottom: 0 }}>
                    {insight && (
                      <div className="callout insight">
                        <span className="icon" aria-hidden="true">
                          →
                        </span>
                        <span>{insight}</span>
                      </div>
                    )}

                    {result.analysis.missesDeadlineCount > 0 && (
                      <div className="callout danger">
                        <span className="icon" aria-hidden="true">
                          !
                        </span>
                        <span>
                          {plural(result.analysis.missesDeadlineCount, 'service')} deliver after the{' '}
                          {shortDate(result.requiredByDate)} required-by date and cannot be used for
                          this load.
                        </span>
                      </div>
                    )}

                    {result.carrierWarnings.map((w) => (
                      <div className="callout warn" key={w}>
                        <span className="icon" aria-hidden="true">
                          !
                        </span>
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}

                {result && result.quotes.length > 0 && (
                  <RateTable
                    result={result}
                    selectedShipVia={selected.selection?.shipVia ?? null}
                    busyShipVia={busyShipVia}
                    onSelect={handleSelect}
                    onBook={handleBook}
                    canBook={canBook}
                  />
                )}

                {result && result.quotes.length === 0 && (
                  <div className="empty">No priced services came back for this lane.</div>
                )}

                {!result && !rating && (
                  <div className="empty">
                    Rate shop this load to compare every eligible carrier service.
                  </div>
                )}

                {result && (
                  <details className="inspector">
                    <summary>Request payload sent to Engine/RateShop/v1</summary>
                    <pre className="json">{JSON.stringify(result.requestPayload, null, 2)}</pre>
                  </details>
                )}
              </div>

              {booking && <BookingPanel result={booking} onClear={handleClearSelection} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
